import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ITMS } from "@/constants/testIds";
import { useDepots, fmtDepot } from "@/lib/depots";
import { Bus, UserCircle, IdentificationCard, MagnifyingGlass, MapPin, Clock, Trash, Warning, X, Calendar, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { MasterScheduleModal, QuickAssignModal } from "@/components/ScheduleModals";

const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const SLOT_MIN = 30; const SLOTS = 48;
const mLbl = (m) => `${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}`;
// Bulk-imported routes have no duration/distance, so fall back to a sensible default
const routeDur = (r) => Math.round(+r?.duration_min) || 60;
const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;
const timeToMin = (v) => { if (!v || !v.includes(":")) return null; const [h,m] = v.split(":").map(Number); return Number.isFinite(h) && Number.isFinite(m) ? h*60+m : null; };

export default function ScheduleDesigner() {
  const depotMap = useDepots();
  const [routes, setRoutes] = useState([]);
  const [trips, setTrips] = useState([]);
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [conductors, setConductors] = useState([]);
  const [tab, setTab] = useState("buses");
  const [qR, setQR] = useState(""); const [qA, setQA] = useState("");
  const [depot, setDepot] = useState("All");
  const [drag, setDrag] = useState(null);
  const [hover, setHover] = useState(null);
  const [hoverTrip, setHoverTrip] = useState(null);
  const [sel, setSel] = useState(null);
  const [newTripPrompt, setNewTripPrompt] = useState(null);
  const [assetDepot, setAssetDepot] = useState("All");
  const [assetShift, setAssetShift] = useState("All");
  const [masterOpen, setMasterOpen] = useState(false);
  const [quickAssign, setQuickAssign] = useState(null); // { day, slotMin }

  const load = async () => {
    const [r,t,b,d,c] = await Promise.all([
      api.get("/routes"), api.get("/trips"), api.get("/fleet/buses"),
      api.get("/crew/drivers"), api.get("/crew/conductors")]);
    setRoutes(r.data); setTrips(t.data); setBuses(b.data); setDrivers(d.data); setConductors(c.data);
  };
  useEffect(()=>{ load(); }, []);

  const depots = useMemo(()=>["All", ...new Set(routes.map(r=>r.depot))], [routes]);
  const filteredRoutes = routes.filter(r =>
    (depot==="All"||r.depot===depot) &&
    (qR===""||r.name.toLowerCase().includes(qR.toLowerCase())||r.route_id.toLowerCase().includes(qR.toLowerCase())));

  // Conflict map: bus or driver used in overlapping slots
  const conflicts = useMemo(()=>{
    const c = new Set();
    for (let i=0;i<trips.length;i++) for (let j=i+1;j<trips.length;j++) {
      const a=trips[i], b=trips[j];
      if (a.day!==b.day) continue;
      const aEnd=a.start_min+a.duration_min, bEnd=b.start_min+b.duration_min;
      const overlap = a.start_min < bEnd && b.start_min < aEnd;
      if (!overlap) continue;
      if (a.bus_id && a.bus_id===b.bus_id) { c.add(a.id); c.add(b.id); }
      if (a.driver_id && a.driver_id===b.driver_id) { c.add(a.id); c.add(b.id); }
      if (a.conductor_id && a.conductor_id===b.conductor_id) { c.add(a.id); c.add(b.id); }
    }
    return c;
  }, [trips]);

  const cellTrips = useMemo(()=>{
    const m={}; for (const t of trips) {
      const s = Math.floor(t.start_min/SLOT_MIN);
      (m[`${t.day}-${s}`] = m[`${t.day}-${s}`] || []).push(t);
    } return m;
  }, [trips]);

  // Drag payload lives in a ref so drop handlers always see the current value (state lags a render
  // behind, and re-rendering the whole grid inside dragstart can make the browser cancel the drag).
  const dragRef = useRef(null);
  const tripsRef = useRef(trips); tripsRef.current = trips;
  const startDrag = (e, d) => {
    dragRef.current = d;
    // Firefox/Safari won't start a drag unless dataTransfer has data
    try { e.dataTransfer.setData("text/plain", d.type); e.dataTransfer.effectAllowed = "copy"; } catch { /* noop */ }
    setTimeout(()=>setDrag(d), 0);
  };
  const endDrag = useCallback(() => { dragRef.current = null; setDrag(null); setHover(null); setHoverTrip(null); }, []);

  const onCellDrop = useCallback((day, slot) => {
    const d = dragRef.current;
    setHover(null);
    if (!d) return;
    if (d.type !== "route") {
      toast.info("Drop buses / drivers / conductors onto a trip. Drag a route into this slot first to create one.");
      endDrag(); return;
    }
    setNewTripPrompt({ route: d.payload, day, start_min: slot*SLOT_MIN, duration_min: routeDur(d.payload) });
    endDrag();
  }, [endDrag]);

  const confirmCreate = async () => {
    const n = newTripPrompt; if (!n) return;
    if (!(n.duration_min >= 1) || !(n.start_min >= 0 && n.start_min < 1440)) { toast.error("Enter a valid start time and duration"); return; }
    try {
      const { data } = await api.post("/trips", { route_id: n.route.route_id, day: n.day, start_min: n.start_min, duration_min: n.duration_min });
      setTrips(p=>[...p, data]); toast.success(`Trip ${data.route_id} ${n.day} ${mLbl(n.start_min)}`);
      setNewTripPrompt(null);
    } catch (e) { toast.error(errMsg(e, "Could not create trip")); }
  };

  const onTripDrop = useCallback(async (trip) => {
    const d = dragRef.current;
    setHoverTrip(null);
    if (!d || d.type === "route") return;
    const field = d.type === "bus" ? "bus_id" : d.type === "driver" ? "driver_id" : "conductor_id";
    const value = d.type === "bus" ? d.payload.fleet_id : d.payload.name;
    if (!value) { toast.error("This asset has no identifier to assign"); endDrag(); return; }

    // Conflict pre-check: find another trip same day where this asset is already assigned with overlap
    const tripEnd = trip.start_min + trip.duration_min;
    const conflict = tripsRef.current.find(t => t.id !== trip.id && t.day === trip.day && t[field] === value &&
      t.start_min < tripEnd && trip.start_min < (t.start_min + t.duration_min));
    if (conflict) {
      const ok = window.confirm(
        `⚠ CONFLICT\n\n${d.type.toUpperCase()} "${value}" is already assigned on ${trip.day} ${mLbl(conflict.start_min)}-${mLbl(conflict.start_min+conflict.duration_min)} (${conflict.route_id}).\n\nThis would overlap the new trip ${mLbl(trip.start_min)}-${mLbl(tripEnd)} (${trip.route_id}).\n\nAssign anyway?`
      );
      if (!ok) { endDrag(); return; }
    }
    try {
      await api.patch(`/trips/${trip.id}`, { [field]: value });
      setTrips(p=>p.map(t => t.id===trip.id ? { ...t, [field]: value } : t));
      setSel(sl => sl && sl.id===trip.id ? { ...sl, [field]: value } : sl);
      toast.success(`${d.type} assigned: ${value}${conflict?" (conflict acknowledged)":""}`);
    } catch (e) { toast.error(errMsg(e, "Assignment failed")); }
    finally { endDrag(); }
  }, [endDrag]);

  const removeTrip = useCallback(async (id) => {
    try { await api.delete(`/trips/${id}`); setTrips(p=>p.filter(t=>t.id!==id)); setSel(sl=>sl && sl.id===id ? null : sl); }
    catch (e) { toast.error(errMsg(e, "Could not delete trip")); }
  }, []);
  const onQuick = useCallback((day, slotMin) => { if (!dragRef.current) setQuickAssign({ day, slotMin }); }, []);

  const assets = tab==="buses"?buses:tab==="drivers"?drivers:conductors;
  const filteredAssets = assets.filter(a => {
    const s = qA.toLowerCase();
    const matchesText = s==="" || (a.fleet_id||"").toLowerCase().includes(s) || (a.name||"").toLowerCase().includes(s) || (a.employee_id||"").toLowerCase().includes(s);
    const matchesDepot = assetDepot==="All" || a.depot===assetDepot;
    const matchesShift = assetShift==="All" || (tab==="buses" ? a.status===assetShift : a.shift_status===assetShift);
    return matchesText && matchesDepot && matchesShift;
  });
  const assetDepots = useMemo(()=>["All", ...new Set(assets.map(a=>a.depot))], [assets]);

  return (
    <div className="flex h-full">
      {/* LEFT — Routes (desktop only — drag-drop won't work on touch) */}
      <aside className="hidden lg:flex w-72 border-r border-slate-200 bg-white flex-col">
        <div className="p-4 border-b border-slate-200"><div className="label-cap text-slate-500">Library</div><h2 className="font-display font-bold text-lg">Routes ({filteredRoutes.length})</h2></div>
        <div className="p-3 space-y-2 border-b border-slate-200">
          <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded-md"><MagnifyingGlass size={14}/><input value={qR} onChange={e=>setQR(e.target.value)} placeholder="Search route…" className="bg-transparent text-sm outline-none flex-1"/></div>
          <select value={depot} onChange={e=>setDepot(e.target.value)} className="w-full px-2 py-1.5 bg-slate-50 rounded-md text-sm outline-none">{depots.map(d=><option key={d}>{d}</option>)}</select>
        </div>
        <div className="flex-1 overflow-auto scroll-thin p-2 space-y-2">
          {filteredRoutes.map(r=>(
            <div key={r.id} draggable onDragStart={(e)=>startDrag(e,{type:"route", payload:r})} onDragEnd={endDrag}
                 className="border border-slate-200 rounded-md p-3 cursor-grab active:cursor-grabbing hover:border-[#1E3A8A] transition-all">
              <div className="flex items-center justify-between"><div className="font-mono text-xs text-[#1E3A8A] font-bold">{r.route_id}</div><span className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded">{r.category}</span></div>
              <div className="font-semibold text-sm mt-1 leading-snug">{r.name}</div>
              <div className="flex items-center gap-3 mt-2 text-xs text-slate-500"><span className="flex items-center gap-1"><MapPin size={11}/>{r.distance_km ? `${r.distance_km}km` : "—"}</span><span className="flex items-center gap-1"><Clock size={11}/>{r.duration_min ? `${Math.round(r.duration_min)}m` : "~60m"}</span><span>·{fmtDepot(r.depot, depotMap)}</span></div>
            </div>
          ))}
        </div>
      </aside>

      {/* CENTER */}
      <section className="flex-1 flex flex-col min-w-0 bg-slate-50">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-slate-200 bg-white flex items-center justify-between gap-2 flex-wrap">
          <div><div className="label-cap text-slate-500">Weekly Plan</div><h1 className="font-display text-lg lg:text-2xl font-bold leading-tight"><span className="hidden sm:inline">Drag routes to grid · Drag assets to trips</span><span className="sm:hidden">Schedule · Tap empty cell to assign</span></h1></div>
          <div className="flex items-center gap-2 flex-wrap">
            {conflicts.size > 0 && <span className="text-xs px-3 py-1.5 bg-red-50 text-red-700 rounded-md flex items-center gap-1.5"><Warning size={12}/>{conflicts.size} conflicts</span>}
            <button data-testid="open-quick-assign-btn" onClick={()=>setQuickAssign({ day: "Mon", slotMin: 360 })}
                    className="lg:hidden px-3 py-2 border border-[#1E3A8A] text-[#1E3A8A] rounded-md text-sm font-semibold flex items-center gap-1.5 hover:bg-blue-50">
              <Plus size={14}/>Assign Trip
            </button>
            <button data-testid="open-master-schedule-btn" onClick={()=>setMasterOpen(true)}
                    className="px-3 py-2 border border-[#1E3A8A] text-[#1E3A8A] rounded-md text-sm font-semibold flex items-center gap-1.5 hover:bg-blue-50">
              <Calendar size={14}/><span className="hidden sm:inline">Master Schedule</span><span className="sm:hidden">Master</span>
            </button>
            <button onClick={()=>toast.success(`Published ${trips.length} trips`)} className="ktcl-btn-primary px-4 py-2 rounded-md text-sm font-semibold">Publish Week</button>
          </div>
        </div>
        <div className="flex-1 overflow-auto scroll-thin">
          <div className="bg-white border border-slate-200 rounded-md m-4 inline-block min-w-[calc(100%-2rem)]">
            <div className="grid sticky top-0 bg-slate-50 z-20 border-b border-slate-200" style={{gridTemplateColumns:"60px repeat(7, minmax(180px, 1fr))"}}>
              <div className="px-2 py-2 label-cap text-slate-500 sticky left-0 bg-slate-50 z-10">Time</div>
              {DAYS.map(d=><div key={d} className="px-3 py-2 text-xs font-semibold border-l border-slate-200">{d}</div>)}
            </div>
            {Array.from({length:SLOTS}).map((_,slot)=>(
              <div key={slot} className="grid border-b border-slate-100 items-stretch" style={{gridTemplateColumns:"60px repeat(7, minmax(180px, 1fr))"}}>
                <div className="px-2 py-1 text-[10px] text-slate-500 font-mono sticky left-0 bg-white z-10 border-r border-slate-100 self-start">{mLbl(slot*SLOT_MIN)}</div>
                {DAYS.map(d=>{
                  const k=`${d}-${slot}`; const list=cellTrips[k]||EMPTY;
                  return (
                    <DayCell key={k} day={d} slot={slot} list={list} conflicts={conflicts}
                      dragType={drag?.type||null} isHover={hover===k}
                      hoverTrip={hoverTrip && list.some(t=>t.id===hoverTrip) ? hoverTrip : null}
                      dragRef={dragRef} setHover={setHover} setHoverTrip={setHoverTrip}
                      onCellDrop={onCellDrop} onTripDrop={onTripDrop} onSelect={setSel} onRemove={removeTrip} onQuick={onQuick}/>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RIGHT — Assets (desktop only) */}
      <aside className="hidden lg:flex w-72 border-l border-slate-200 bg-white flex-col">
        <div className="p-4 border-b border-slate-200"><div className="label-cap text-slate-500">Assets</div><h2 className="font-display font-bold text-lg">Drag onto trip</h2></div>
        <div className="flex border-b border-slate-200 text-xs">
          {[["buses","Buses",buses.length,Bus],["drivers","Drivers",drivers.length,UserCircle],["conductors","Cond.",conductors.length,IdentificationCard]].map(([k,l,n,I])=>(
            <button key={k} onClick={()=>setTab(k)} className={`flex-1 py-2.5 flex items-center justify-center gap-1.5 ${tab===k?"border-b-2 border-[#1E3A8A] text-[#1E3A8A] font-semibold":"text-slate-500"}`}><I size={14}/>{l} ({n})</button>
          ))}
        </div>
        <div className="p-2 border-b border-slate-200 space-y-1.5">
          <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded-md"><MagnifyingGlass size={12}/><input value={qA} onChange={e=>setQA(e.target.value)} placeholder="Search…" className="bg-transparent text-sm outline-none flex-1"/></div>
          <div className="grid grid-cols-2 gap-1.5">
            <select value={assetDepot} onChange={e=>setAssetDepot(e.target.value)} className="px-2 py-1 bg-slate-50 border border-slate-200 rounded text-[11px] outline-none">
              {assetDepots.map(d=><option key={d}>{d}</option>)}
            </select>
            <select value={assetShift} onChange={e=>setAssetShift(e.target.value)} className="px-2 py-1 bg-slate-50 border border-slate-200 rounded text-[11px] outline-none">
              {(tab==="buses" ? ["All","Available","On Trip","Maintenance","Breakdown","Charging","Reserved"] : ["All","On Duty","Off Duty","On Break"]).map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="text-[10px] text-slate-500 px-1">Showing {filteredAssets.length} of {assets.length}</div>
        </div>
        <div className="flex-1 overflow-auto scroll-thin p-2 space-y-2">
          {filteredAssets.map((a)=>(
            <div key={a.id} draggable
                 onDragStart={(e)=>startDrag(e,{type: tab==="buses"?"bus":tab==="drivers"?"driver":"conductor", payload:a})}
                 onDragEnd={endDrag}
                 className="border border-slate-200 rounded-md p-2.5 cursor-grab active:cursor-grabbing hover:border-[#1E3A8A] transition-all">
              {tab==="buses" ? <>
                <div className="flex items-center justify-between"><div className="font-mono text-xs font-bold text-[#1E3A8A]">{a.fleet_id}</div><span className="text-[10px]">{a.status}</span></div>
                <div className="text-xs text-slate-600">{a.registration_number}</div>
                <div className="text-[10px] text-slate-500 mt-1">{a.category}{a.soc!=null ? ` · 🔋${a.soc}%` : ""} · {fmtDepot(a.depot, depotMap)}</div>
              </> : <>
                <div className="flex items-center justify-between"><div className="font-semibold text-sm">{a.name}</div><span className={`text-[10px] px-1.5 py-0.5 rounded ${a.shift_status==="On Duty"?"bg-green-50 text-green-700":"bg-slate-100 text-slate-600"}`}>{a.shift_status}</span></div>
                <div className="text-xs text-slate-500">{a.employee_id} · {fmtDepot(a.depot, depotMap)}</div>
                <div className="text-[10px] text-slate-500 mt-1">Duty: {a.duty_hours}h{a.license_number?` · LIC ${a.license_number}`:""}</div>
              </>}
            </div>
          ))}
        </div>
      </aside>

      {/* Trip side panel (desktop) / bottom sheet (mobile) */}
      {sel && (
        <>
          {/* Mobile backdrop */}
          <div className="lg:hidden fixed inset-0 bg-black/50 z-[1200]" onClick={()=>setSel(null)}/>
          <div className="
              fixed z-[1210] bg-white shadow-2xl flex flex-col
              max-lg:inset-x-0 max-lg:bottom-0 max-lg:rounded-t-2xl max-lg:max-h-[85dvh]
              lg:top-14 lg:right-0 lg:bottom-0 lg:w-96 lg:border-l lg:border-slate-200">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-2 max-lg:rounded-t-2xl relative">
              <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-10 h-1 bg-slate-300 rounded-full lg:hidden"/>
              <div className="min-w-0 mt-1 lg:mt-0">
                <div className="label-cap text-slate-500">Trip</div>
                <h3 className="font-display font-bold truncate">{sel.route_id} · {sel.day}</h3>
              </div>
              <button onClick={()=>setSel(null)} className="text-slate-400 hover:text-slate-700 p-1 shrink-0 mt-1 lg:mt-0"><X size={20}/></button>
            </div>
            <div className="p-4 space-y-3 flex-1 overflow-auto overscroll-contain" style={{ WebkitOverflowScrolling: "touch" }}>
              <Row k="Route" v={sel.route_name}/>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 items-center text-sm">
                <span className="text-slate-500 whitespace-nowrap">Start Time</span>
                <input key={`${sel.id}-t`} type="time" defaultValue={mLbl(sel.start_min)} onBlur={async e=>{const s=timeToMin(e.target.value); if (s===null || s===sel.start_min) return; try { await api.patch(`/trips/${sel.id}`,{start_min:s}); setTrips(p=>p.map(t=>t.id===sel.id?{...t,start_min:s}:t)); setSel(x=>({...x,start_min:s})); toast.success("Time updated"); } catch (er) { toast.error(errMsg(er,"Time update failed")); e.target.value = mLbl(sel.start_min); }}} className="px-2 py-1.5 bg-slate-50 border border-slate-200 rounded text-sm min-w-0 w-full"/>
                <span className="text-slate-500 whitespace-nowrap">Duration (min)</span>
                <input key={`${sel.id}-d`} type="number" min="1" defaultValue={sel.duration_min} onBlur={async e=>{const d=Math.round(+e.target.value); if (!(d>=1) || d===sel.duration_min) { e.target.value = sel.duration_min; return; } try { await api.patch(`/trips/${sel.id}`,{duration_min:d}); setTrips(p=>p.map(t=>t.id===sel.id?{...t,duration_min:d}:t)); setSel(x=>({...x,duration_min:d})); toast.success("Duration updated"); } catch (er) { toast.error(errMsg(er,"Duration update failed")); e.target.value = sel.duration_min; }}} className="px-2 py-1.5 bg-slate-50 border border-slate-200 rounded text-sm min-w-0 w-full"/>
              </div>
              <EditRow k="Bus" v={sel.bus_id} onClear={()=>save(sel.id,"bus_id","",setTrips,setSel)}/>
              <EditRow k="Driver" v={sel.driver_id} onClear={()=>save(sel.id,"driver_id","",setTrips,setSel)}/>
              <EditRow k="Conductor" v={sel.conductor_id} onClear={()=>save(sel.id,"conductor_id","",setTrips,setSel)}/>
              {conflicts.has(sel.id) && <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded"><b>Conflict:</b> bus/driver/conductor double-booked in overlapping slot. Reassign to resolve.</div>}
              <div className="text-xs text-slate-500 pt-2 border-t hidden lg:block">Tip: drag a bus/driver/conductor card from the right panel onto this trip in the grid to assign.</div>
              <div className="text-xs text-slate-500 pt-2 border-t lg:hidden">Tip: tap an empty cell on the grid to add a new trip. Drag-and-drop assignment works on desktop.</div>
            </div>
            <div className="p-4 border-t border-slate-200">
              <button onClick={()=>removeTrip(sel.id)} className="w-full px-4 py-2 border border-red-200 text-red-600 rounded-md text-sm hover:bg-red-50">Delete Trip</button>
            </div>
          </div>
        </>
      )}
      {newTripPrompt && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={()=>setNewTripPrompt(null)}>
          <div className="bg-white rounded-lg p-5 w-full max-w-md space-y-3" onClick={e=>e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg">New Trip · {newTripPrompt.route.route_id}</h3>
            <div className="text-sm text-slate-600">{newTripPrompt.route.name} · {newTripPrompt.day}</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-slate-500 mb-1">Start Time</div><input type="time" defaultValue={mLbl(newTripPrompt.start_min)} onChange={e=>{const m=timeToMin(e.target.value); if (m!==null) setNewTripPrompt(p=>({...p,start_min:m}));}} className="w-full px-2 py-1.5 bg-slate-50 border border-slate-200 rounded"/></div>
              <div><div className="text-xs text-slate-500 mb-1">Duration (min)</div><input type="number" min="1" defaultValue={newTripPrompt.duration_min} onChange={e=>setNewTripPrompt(p=>({...p,duration_min:Math.round(+e.target.value)}))} className="w-full px-2 py-1.5 bg-slate-50 border border-slate-200 rounded"/></div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={()=>setNewTripPrompt(null)} className="px-3 py-1.5 border border-slate-200 rounded text-sm">Cancel</button>
              <button onClick={confirmCreate} className="ktcl-btn-primary px-4 py-1.5 rounded text-sm font-semibold">Create Trip</button>
            </div>
          </div>
        </div>
      )}

      <MasterScheduleModal
        open={masterOpen}
        onClose={()=>setMasterOpen(false)}
        onCreated={()=>{ load(); }}
        routes={routes} buses={buses} drivers={drivers} conductors={conductors}
      />
      <QuickAssignModal
        open={!!quickAssign}
        day={quickAssign?.day}
        slotMin={quickAssign?.slotMin}
        onClose={()=>setQuickAssign(null)}
        onCreated={(t)=>{ setTrips(p=>[...p, t]); }}
        routes={routes} buses={buses} drivers={drivers} conductors={conductors}
      />
    </div>
  );
}

const EMPTY = [];

// Memoised so hover/drag state changes only re-render the one or two cells that actually changed
const DayCell = memo(function DayCell({ day, slot, list, conflicts, dragType, isHover, hoverTrip, dragRef, setHover, setHoverTrip, onCellDrop, onTripDrop, onSelect, onRemove, onQuick }) {
  const k = `${day}-${slot}`;
  return (
    <div onDragOver={e=>{e.preventDefault(); if(dragType==="route") setHover(k);}}
         onDragLeave={e=>{ if (!e.currentTarget.contains(e.relatedTarget)) setHover(h=>h===k?null:h); }}
         onDrop={e=>{e.preventDefault(); onCellDrop(day, slot);}}
         onClick={(e)=>{ if (list.length === 0) { e.stopPropagation(); onQuick(day, slot*SLOT_MIN); } }}
         className={`trip-cell min-h-[40px] border-l border-slate-100 p-0.5 flex flex-col gap-0.5 overflow-visible cursor-pointer hover:bg-blue-50/30 ${isHover?"drag-over":""}`}>
      {list.map(t=>{
        const conf = conflicts.has(t.id);
        return (
          <div key={t.id} onClick={(e)=>{e.stopPropagation(); onSelect(t);}}
               // Assets (bus/driver/conductor) are handled by the trip tile; route drops fall through to the cell so you can add another trip to a busy slot
               onDragOver={e=>{ if (dragType && dragType!=="route") { e.preventDefault(); e.stopPropagation(); setHoverTrip(t.id); } }}
               onDragLeave={e=>{ if (!e.currentTarget.contains(e.relatedTarget)) setHoverTrip(h=>h===t.id?null:h); }}
               onDrop={(e)=>{ if (dragRef.current && dragRef.current.type!=="route") { e.preventDefault(); e.stopPropagation(); onTripDrop(t); } }}
               className={`bg-[#EFF6FF] border-l-2 border-l-[#1E3A8A] rounded-sm px-1.5 py-0.5 text-[10px] flex items-center justify-between group cursor-pointer shrink-0 ${conf?"ring-2 ring-[#EF4444]":""} ${hoverTrip===t.id?"bg-blue-200 ring-2 ring-[#1E3A8A]":""}`}>
            <div className="leading-tight min-w-0 flex-1">
              <div className="font-semibold truncate flex items-center gap-1"><span className="font-mono">{t.route_id}</span><span className="text-slate-500 font-normal">{mLbl(t.start_min)}</span>{conf && <Warning size={9} className="text-[#EF4444]"/>}</div>
              <div className="text-slate-600 truncate text-[9px]">{t.bus_id||"·no bus"} · {(t.driver_id||"·no driver").split(" ")[0]}</div>
            </div>
            <button onClick={(e)=>{e.stopPropagation(); onRemove(t.id);}} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-[#EF4444]"><Trash size={10}/></button>
          </div>
        );
      })}
    </div>
  );
});

async function save(id, field, value, setTrips, setSel) {
  try {
    await api.patch(`/trips/${id}`, { [field]: value });
    setTrips(p=>p.map(t=>t.id===id?{...t,[field]:value}:t));
    setSel(s=>s?{...s,[field]:value}:s);
    toast.success("Updated");
  } catch (e) { toast.error(errMsg(e, "Update failed")); }
}
const Row = ({k,v}) => (
  <div className="flex justify-between items-center gap-3 text-sm min-w-0">
    <span className="text-slate-500 shrink-0">{k}</span>
    <span className="font-semibold text-right truncate min-w-0">{v||"—"}</span>
  </div>
);
const EditRow = ({k,v,onClear}) => (
  <div className="flex justify-between items-center gap-2 text-sm min-w-0">
    <span className="text-slate-500 shrink-0">{k}</span>
    <div className="flex items-center gap-2 min-w-0 flex-1 justify-end">
      <span className="font-semibold truncate min-w-0">{v||<span className="text-slate-400 italic">unassigned</span>}</span>
      {v && <button onClick={onClear} className="text-xs text-red-600 hover:underline shrink-0">clear</button>}
    </div>
  </div>
);
